import React, { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip } from "recharts";
import { isEmpty } from "lodash";

import { useStateSelector } from "utils/field";
import { exportCSV } from "utils/csv";
import { buildCommunityResultsChartCsv } from "utils/communities/buildCommunityResultsChartCsv";
import { buildCommunityAllResultsCsv } from "utils/parametricResultRows";
import { getMaxValueForGraphMetric, getResults, organizeDataForDisplay } from "utils/communities";
import {
    COMMUNITY_GRAPH_TOP_LABELS,
    COMMUNITY_GRAPH_LABELS,
    COMMUNITY_GRAPH_LEGEND_LABELS,
    COMMUNITY_GRAPH_TOP_COLOURS,
    COMMUNITY_GRAPH_TOP_COLOURS_ENVIRO_CENTRE,
} from "utils/communities/constants";

import CommResultsFilters from "../../Filters";
import SyncScrollBar from "components/SyncScrollBar";
import ToggleMulti from "components/ToggleMulti";
import NoData from "../NoData";

import classes from "./style.module.scss";

const UpperGraph = ({
    runData,
    allModels = [],
    allPackages = {},
    fuelPrices = {},
    communityModelCountsMap = {},
    ecmInputMap = {},
    applyModelResultMultiplier,
    setApplyModelResultMultiplier,
}) => {
    const [graphMetric, setGraphMetric] = useState("energy"); // energy, emissions, opCost
    const [graphStyle, setGraphStyle] = useState("clustered"); // clustered, stacked
    const [graphEnergyPV, setGraphEnergyPV] = useState([false, true]); // [false, true], [false], [true], []
    const [elecDisplayUnit, setElecDisplayUnit] = useState("KWH");
    const [ngDisplayUnit, setNgDisplayUnit] = useState("M3");
    const [selectedModels, setSelectedModels] = useState(allModels.map((model) => model.value));
    const [results, setResults] = useState(null);
    const [displayData, setDisplayData] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isLoadingCsvData, setIsLoadingCsvData] = useState(false);

    const scrollContainerRef = useRef(null);

    const { email = "" } = useStateSelector("user") || {};

    var isEnviroCentreUser = email.split("@")[1] === "@envirocentre.ca";

    const graphColours = isEnviroCentreUser ? COMMUNITY_GRAPH_TOP_COLOURS_ENVIRO_CENTRE : COMMUNITY_GRAPH_TOP_COLOURS;

    useEffect(() => {
        if (!runData) {
            return;
        }

        const results = getResults(selectedModels, runData, fuelPrices, communityModelCountsMap);
        setResults(results);
    }, [selectedModels, runData]);

    useEffect(() => {
        if (!results) {
            return;
        }

        const displayData = organizeDataForDisplay(results, allPackages, applyModelResultMultiplier);

        setDisplayData(displayData);
    }, [results, graphMetric, applyModelResultMultiplier]);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
        ro.observe(el);
        setContainerWidth(el.clientWidth);

        return () => ro.disconnect();
    }, []);

    const MIN_SECTION_WIDTH = 150;
    const sectionWidth =
        displayData.length > 0 ? Math.max(MIN_SECTION_WIDTH, containerWidth / displayData.length) : MIN_SECTION_WIDTH;
    const chartWidth = displayData.length * sectionWidth;

    const legendItems = [
        ...(COMMUNITY_GRAPH_LEGEND_LABELS[graphMetric]?.top || []),
        { isSeparator: true },
        ...(COMMUNITY_GRAPH_LEGEND_LABELS[graphMetric]?.bottom || []),
    ];

    const rawMax = getMaxValueForGraphMetric(displayData, graphMetric);
    const maxValue = Math.ceil(rawMax * 1.1) || 100;

    const CHART_HEIGHT = 270;
    const calculateYTicks = () => {
        const tickCount = 6;
        const ticks = [];

        for (let i = 0; i <= tickCount; i++) {
            const value = (maxValue / tickCount) * i;
            const positionFromTop = ((tickCount - i) / tickCount) * CHART_HEIGHT;

            ticks.push({ value, position: positionFromTop });
        }
        return ticks;
    };

    const yTicks = calculateYTicks();

    const formatYAxisTick = (value) => {
        return value.toLocaleString();
    };

    const chartMargins = { top: 0, right: 0, left: 0, bottom: 0 };

    const updateSelectedModels = (value) => {
        setSelectedModels(value);
    };

    const sortBarChartTooltip = (item, graphMetric) => {
        var dataKeyOrder = [];

        if (graphMetric === "energy") {
            dataKeyOrder = [`elecGJ`, `ngGJ`];

            if (graphEnergyPV.length >= 1) {
                if (!graphEnergyPV[0]) {
                    dataKeyOrder += [`aecGJ`, `netGJ`];
                } else {
                    dataKeyOrder += [`netGJ`, `aecGJ`];
                }
            }
        } else if (graphMetric === "opEmission") {
            dataKeyOrder = [`totalEmissionsTCO2`];
        } else if (graphMetric === "opCost") {
            dataKeyOrder = [`electricityCost`, `naturalGasCost`];
        }
        return dataKeyOrder.indexOf(item.dataKey);
    };

    const omitItem = (item) => {
        const omitWithPV = item.dataKey.includes("net") && !graphEnergyPV.includes(true);
        const omitWithoutPV = item.dataKey.includes("aec") && !graphEnergyPV.includes(false);
        return omitWithPV || omitWithoutPV;
    };

    const hasExportableRunData = () => {
        const hasWholePackages = runData?.runResults && !isEmpty(runData.runResults);
        const hasByMeasure = Array.isArray(runData?.runResultsByMeasure) && runData.runResultsByMeasure.length > 0;
        return runData && (hasWholePackages || hasByMeasure);
    };

    const handleExportChartData = () => {
        if (!hasExportableRunData()) {
            return;
        }

        setIsLoadingCsvData(true);

        const csvDataToExport = buildCommunityResultsChartCsv(runData, selectedModels, {
            allPackages,
            communityModelCountsMap,
            fuelPrices,
            applyModelResultMultiplier,
        });

        exportCSV(csvDataToExport, "community_results_charts.csv");

        setIsLoadingCsvData(false);
    };

    const handleExportRawResults = () => {
        if (!hasExportableRunData()) {
            return;
        }

        setIsLoadingCsvData(true);

        const csvDataToExport = buildCommunityAllResultsCsv(runData, selectedModels, {
            allModels,
            communityModelCountsMap,
            allPackages,
            ecmInputMap,
        });

        exportCSV(csvDataToExport, "community_all_results.csv");

        setIsLoadingCsvData(false);
    };

    return (
        <div className={classes.upperGraphWrapper}>
            <CommResultsFilters
                modelsOptions={allModels}
                setSelectedModels={updateSelectedModels}
                selectedModels={selectedModels}
                selectedMetric={graphMetric}
                setSelectedMetric={setGraphMetric}
                selectedPV={graphEnergyPV}
                setSelectedPV={setGraphEnergyPV}
                graphStyle={graphStyle}
                setGraphStyle={setGraphStyle}
                applyModelResultMultiplier={applyModelResultMultiplier}
                setApplyModelResultMultiplier={setApplyModelResultMultiplier}
                onExportChartData={handleExportChartData}
                onExportRawResults={handleExportRawResults}
                isLoadingCsvData={isLoadingCsvData}
                exportDisabled={!hasExportableRunData()}
            />
            <div className={classes.upperGraphContainer}>
                <div className={classes.upperGraphLeft}>
                    <div className={classes.titleSpacer} />
                    <div className={classes.upperGraphLeftTop}>
                        <span className={classes.upperGraphLeftTopLabel}>
                            {COMMUNITY_GRAPH_LABELS[graphMetric] || "GJ/year"}
                        </span>
                        <div className={classes.yAxisTicksContainer}>
                            {yTicks.map((tick, index) => (
                                <div key={index} className={classes.yAxisTick} style={{ top: `${tick.position}px` }}>
                                    <span className={classes.yAxisTickLabel}>
                                        {formatYAxisTick(tick.value.toFixed(0))}
                                    </span>
                                    <span className={classes.yAxisTickLine} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={classes.emptyCell} />
                    <div className={classes.upperGraphLeftBottom}>
                        {legendItems.map((item, index) => {
                            if (item.isSeparator) {
                                return <div key={`sep-${index}`} className={classes.separator} />;
                            }

                            if (["aecGJ", "netGJ"].includes(item.dataKey) && omitItem(item)) {
                                return <></>;
                            }

                            const isElec = ["elecGJ", "elecKWH"].includes(item.dataKey);
                            const isGas = ["ngGJ", "ngM3"].includes(item.dataKey);

                            const energyDisplayUnit = isElec ? elecDisplayUnit : isGas ? ngDisplayUnit : "GJ";
                            const setEnergyDisplayUnit = isElec
                                ? setElecDisplayUnit
                                : isGas
                                  ? setNgDisplayUnit
                                  : () => {};
                            const familiarUnit = isElec ? "KWH" : "M3";
                            const label = (isElec || isGas) && energyDisplayUnit === "GJ" ? item.labelGJ : item.label;

                            return (
                                <div className={`${classes.regularCellBase} ${classes.legendCell}`} key={index}>
                                    <div
                                        className={classes.legendCellColour}
                                        style={{
                                            backgroundColor:
                                                (isEnviroCentreUser ? item.colourEC : item.colour) || "transparent",
                                            border: item.colour !== "#fff" ? "1px solid black" : "",
                                        }}
                                    />
                                    <span>{label}</span>
                                    {(isElec || isGas) && (
                                        <ToggleMulti
                                            className={classes.energyUnitToggle}
                                            type={"small"}
                                            active={energyDisplayUnit === "GJ" ? "GJ" : familiarUnit}
                                            options={[
                                                {
                                                    id: familiarUnit,
                                                    label: isElec ? "kWh" : "m³",
                                                    onClick: () => setEnergyDisplayUnit(familiarUnit),
                                                },
                                                {
                                                    id: "GJ",
                                                    label: "GJ",
                                                    onClick: () => setEnergyDisplayUnit("GJ"),
                                                },
                                            ]}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={classes.upperGraphRight}>
                    <div className={classes.upperGraphRightTitle}>
                        <span className={classes.upperGraphRightTitleText}>
                            {COMMUNITY_GRAPH_TOP_LABELS[graphMetric] || "Energy Consumption Comparison"}
                        </span>
                    </div>
                    <div className={classes.scrollContainer} ref={scrollContainerRef} style={{ position: "relative" }}>
                        {isEmpty(displayData) && <NoData />}
                        <div style={{ width: `${chartWidth}px`, borderRight: "1px solid #e1eaf0" }}>
                            <div className={classes.chartWrapper}>
                                <svg className={classes.gridLines} style={{ width: chartWidth, height: 270 }}>
                                    {yTicks.map((tick, index) => (
                                        <line
                                            key={`grid-${index}`}
                                            x1={0}
                                            y1={tick.position}
                                            x2={chartWidth}
                                            y2={tick.position}
                                            stroke="#e1eaf0"
                                            strokeWidth={1}
                                        />
                                    ))}
                                </svg>
                                <div className={classes.barChartWrapper}>
                                    <BarChart
                                        width={chartWidth}
                                        height={270}
                                        data={displayData}
                                        margin={chartMargins}
                                        barCategoryGap="20%"
                                        barGap={0}
                                    >
                                        <XAxis dataKey="packageName" tick={false} axisLine={false} height={1} />
                                        {/* Tooltip for the bars can be disabled here */}
                                        <Tooltip
                                            itemSorter={(item) => sortBarChartTooltip(item, graphMetric)}
                                            itemStyle={isEnviroCentreUser ? { color: "black" } : {}}
                                        />
                                        <YAxis
                                            tick={false}
                                            axisLine={false}
                                            width={1}
                                            domain={[0, maxValue]}
                                            tickCount={6}
                                        />

                                        {graphMetric === "energy" && (
                                            <>
                                                <Bar
                                                    name={`Electricity (GJ)`}
                                                    dataKey={`elecGJ`}
                                                    fill={graphColours.elecGJ}
                                                    barSize={35}
                                                    stackId={graphStyle === "stacked" ? "a" : undefined}
                                                    stroke="#000"
                                                    strokeWidth={0.5}
                                                />
                                                <Bar
                                                    name={`Natural Gas (GJ)`}
                                                    dataKey={`ngGJ`}
                                                    fill={graphColours.ngGJ}
                                                    barSize={35}
                                                    stackId={graphStyle === "stacked" ? "a" : undefined}
                                                    stroke="#000"
                                                    strokeWidth={0.5}
                                                />
                                                {graphStyle !== "stacked" && (
                                                    <>
                                                        {graphEnergyPV.includes(false) && (
                                                            <Bar
                                                                name={`Total Energy w/o PV (GJ)`}
                                                                dataKey={`aecGJ`}
                                                                fill={graphColours.aecGJ}
                                                                barSize={35}
                                                                stackId={graphStyle === "stacked" ? "a" : undefined}
                                                                stroke="#000"
                                                                strokeWidth={0.5}
                                                            />
                                                        )}
                                                        {graphEnergyPV.includes(true) && (
                                                            <Bar
                                                                name={`Total Energy w/ PV (GJ)`}
                                                                dataKey={`netGJ`}
                                                                fill={graphColours.netGJ}
                                                                barSize={35}
                                                                stackId={graphStyle === "stacked" ? "a" : undefined}
                                                                stroke="#000"
                                                                strokeWidth={0.5}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {graphMetric === "opEmissions" && (
                                            <>
                                                <Bar
                                                    dataKey="totalEmissionsTCO2"
                                                    fill={graphColours.totalEmissionsTCO2}
                                                    barSize={35}
                                                    stackId={graphStyle === "stacked" ? "a" : undefined}
                                                    stroke="#000"
                                                    strokeWidth={0.5}
                                                />
                                            </>
                                        )}
                                        {graphMetric === "opCost" && (
                                            <>
                                                <Bar
                                                    dataKey="electricityCost"
                                                    fill={graphColours.electricityCost}
                                                    barSize={35}
                                                    stackId={graphStyle === "stacked" ? "a" : undefined}
                                                    stroke="#000"
                                                    strokeWidth={0.5}
                                                />
                                                <Bar
                                                    dataKey="naturalGasCost"
                                                    fill={graphColours.naturalGasCost}
                                                    barSize={35}
                                                    stackId={graphStyle === "stacked" ? "a" : undefined}
                                                    stroke="#000"
                                                    strokeWidth={0.5}
                                                />
                                            </>
                                        )}
                                    </BarChart>
                                </div>
                                <svg className={classes.verticalSeparators} style={{ width: chartWidth, height: 270 }}>
                                    {displayData.map((item, index) => {
                                        if (index < displayData.length - 1) {
                                            const xPosition = (index + 1) * sectionWidth;
                                            return (
                                                <line
                                                    key={`separator-${item.packageId}`}
                                                    x1={xPosition}
                                                    y1={0}
                                                    x2={xPosition}
                                                    y2={270}
                                                    stroke="#e1eaf0"
                                                    strokeWidth={1}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                </svg>
                            </div>

                            <div className={classes.tableHeaderRow}>
                                {displayData.map((item) => (
                                    <div
                                        key={item.packageId}
                                        className={classes.tableHeaderCell}
                                        style={{ width: `${sectionWidth}px`, minWidth: `${sectionWidth}px` }}
                                    >
                                        {item.packageName}
                                    </div>
                                ))}
                            </div>

                            <div className={classes.tableDataContainer}>
                                {legendItems.map((rowItem, rowIndex) => {
                                    if (rowItem.isSeparator) {
                                        return <div key={`sep-${rowIndex}`} className={classes.separator} />;
                                    }

                                    if (["aecGJ", "netGJ"].includes(rowItem.dataKey) && omitItem(rowItem)) {
                                        return <></>;
                                    }

                                    const isElec = ["elecGJ", "elecKWH"].includes(rowItem.dataKey);
                                    const isGas = ["ngGJ", "ngM3"].includes(rowItem.dataKey);

                                    const energyDisplayUnit = isElec ? elecDisplayUnit : isGas ? ngDisplayUnit : "GJ";
                                    const dataKey =
                                        (isElec || isGas) && energyDisplayUnit === "GJ"
                                            ? rowItem.dataKeyGJ
                                            : rowItem.dataKey;

                                    return (
                                        <div className={classes.tableRow} key={rowIndex}>
                                            {displayData.map((item) => (
                                                <div
                                                    key={item.packageId}
                                                    className={classes.tableCell}
                                                    style={{
                                                        width: `${sectionWidth}px`,
                                                        minWidth: `${sectionWidth}px`,
                                                    }}
                                                >
                                                    {item[dataKey]}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <SyncScrollBar targetRef={scrollContainerRef} />
                </div>
            </div>
        </div>
    );
};

export default UpperGraph;
